import { Check as CheckIcon } from '@mui/icons-material';
import { Switch } from '@mui/material';
import { set } from 'lodash/fp';
import { Drawer } from 'src/components/drawer';
import { EmptyProvider } from 'src/components/emptyProvider';
import { SidebarHeader } from 'src/components/header';
import { SimpleDialog } from 'src/components/modal';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { useModal } from 'src/hooks';
import { lang, useUserRole } from 'src/utils';

import {
  IDefaultSection,
  IGridSection,
  IScheduledSectionComponent,
} from '../../ScheduleComponentPage.interface';
import { defaultDrawerProps } from '../screensDrawer';
import { StyledWarningIcon } from '../screensDrawer/ScreensDrawer.styled';
import { validateScreenStatus } from './ScreenSectionsDrawer.helper';
import {
  ScheduleComponentButton,
  SectionContainer,
  SectionContentWrapper,
  SectionDescription,
  SectionName,
  SectionNameContainer,
  SectionNameWrapper,
  SectionsWrapper,
  StyledDeleteIcon,
  StyledGridViewIcon,
} from './ScreenSectionsDrawer.styled';

export interface IScreenSectionsDrawer {
  screenIndex: number;
  showScreenSectionsDrawer: boolean;
  screenSections: (IGridSection | IDefaultSection)[];
  options?: {
    showToggleButton?: boolean;
    overrideButtonTitle?: string;
  };
  onCloseScreenSectionsDrawer: () => void;
  onDeleteScreenSection: (value: string) => void;
  onScheduleSectionComponent: (sectionIndex: number) => void;
  onUpdateScreensGridSectionsWhenSectionChanges: (value: any) => void;
  isAbleToEditSection?: (
    components: IScheduledSectionComponent[] | undefined,
  ) => boolean;
}

export const ScreenSectionsDrawer = (props: IScreenSectionsDrawer) => {
  const {
    screenIndex,
    showScreenSectionsDrawer,
    screenSections,
    options,
    onCloseScreenSectionsDrawer,
    onDeleteScreenSection,
    onScheduleSectionComponent,
    onUpdateScreensGridSectionsWhenSectionChanges,
    isAbleToEditSection,
  } = props;
  const showToggleButton = options?.showToggleButton ?? true;

  const removeSectionModal = useModal();

  const { isOwnerOperator } = useUserRole();

  const handleUpdateScreenSection = (
    values: Record<string, unknown>,
    screenSectionIndex: number,
  ) => {
    let updatedSection = screenSections[screenSectionIndex];

    Object.entries(values).forEach(([field, value]) => {
      updatedSection = set(field, value, updatedSection);
    });

    onUpdateScreensGridSectionsWhenSectionChanges(updatedSection);
  };

  const handleDeleteScreenSection = (id: string) => {
    onDeleteScreenSection(id);
    removeSectionModal.onCloseModal();
  };

  const handleUpdatePredefinedSection = (value: boolean, index: number) => {
    const updatedData = {
      canBeScheduled: value,
      ...(!value && { components: [] }),
    };

    handleUpdateScreenSection(updatedData, index);
  };

  const filteredSections = screenSections.filter((section) => {
    if (section.isDisclaimer) {
      if (isOwnerOperator) {
        return (
          Array.isArray(section.components) && section.components.length > 0
        );
      }

      return true;
    }

    return true;
  });

  return (
    <Drawer open={showScreenSectionsDrawer} {...defaultDrawerProps}>
      {showScreenSectionsDrawer && (
        <>
          <SidebarHeader
            label={`${lang.screen} ${screenIndex + 1}`}
            onBackClick={onCloseScreenSectionsDrawer}
          />

          <EmptyProvider
            message={lang.scheduleComponent.emptySection}
            dataToCheck={screenSections}>
            <SectionsWrapper>
              {filteredSections.map((section) => {
                const originalIndex = screenSections.indexOf(section);
                const {
                  hasComponents,
                  enabledScheduleButton,
                  showWarningIcon,
                  showCheckIcon,
                  hasOverrideDateRange,
                } = validateScreenStatus(
                  section,
                  isAbleToEditSection,
                  isOwnerOperator,
                );

                const scheduleButtonText = options?.overrideButtonTitle
                  ? options.overrideButtonTitle
                  : hasComponents
                  ? lang.editSchedule
                  : lang.scheduleComponent.label;

                return (
                  <SectionContainer key={section.i}>
                    <SectionContentWrapper>
                      <SectionNameWrapper>
                        <StyledGridViewIcon />

                        <SectionNameContainer>
                          <SectionName variant="subtitle2">
                            {section?.name}
                          </SectionName>

                          {!section?.isDisclaimer && (
                            <SectionDescription variant="body2">
                              {`${section.w! * section.h!} ${lang.selected}`}
                            </SectionDescription>
                          )}
                        </SectionNameContainer>
                        {showWarningIcon && (
                          <CustomTooltip
                            placement="right"
                            skipValidateOverflow
                            title={
                              isOwnerOperator
                                ? lang.componentRequireStartEndDate
                                : lang.componentRequireStartDate
                            }>
                            <StyledWarningIcon data-testid="WarningIcon" />
                          </CustomTooltip>
                        )}

                        {showCheckIcon &&
                          (!isOwnerOperator || hasOverrideDateRange) && (
                            <CheckIcon color="success" />
                          )}
                      </SectionNameWrapper>

                      {section.canBeDeleted ? (
                        <StyledDeleteIcon
                          color="error"
                          onClick={() => {
                            removeSectionModal.onOpenModal(() => {
                              handleDeleteScreenSection(section.i);
                            });
                          }}
                        />
                      ) : showToggleButton ? (
                        <Switch
                          checked={!!section?.canBeScheduled}
                          onChange={(event) =>
                            handleUpdatePredefinedSection(
                              !!event?.target?.checked,
                              originalIndex,
                            )
                          }
                        />
                      ) : null}
                    </SectionContentWrapper>

                    <ScheduleComponentButton
                      variant="outlined"
                      data-testid={`section-schedule-component-${
                        originalIndex + 1
                      }`}
                      disabled={!enabledScheduleButton}
                      onClick={() => onScheduleSectionComponent(originalIndex)}>
                      {scheduleButtonText}
                    </ScheduleComponentButton>
                  </SectionContainer>
                );
              })}
            </SectionsWrapper>
          </EmptyProvider>
        </>
      )}

      {removeSectionModal.open && (
        <SimpleDialog
          actionColor="error"
          open={removeSectionModal.open}
          cancelText={lang.cancel}
          confirmText={lang.deleteText}
          title={lang.scheduleComponent.deleteSection}
          subTitle={lang.scheduleComponent.deleteSectionDescription}
          onClose={removeSectionModal.onCloseModal}
          onClickCancel={removeSectionModal.onCloseModal}
          modalSize="sm"
          onClickConfirm={removeSectionModal.customFn}
          adjustTitlePadding={true}
        />
      )}
    </Drawer>
  );
};
